// Chi fornisce la merce di un ordine, e quali fornitori il filtro della pagina
// Ordini deve offrire.
//
// Fino al 2026-09-04 le opzioni erano la lista STATICA dei tab con
// ``orderFilter`` e il match guardava solo ``supplier_kind`` (5 valori). Ma il
// PIM sa di chi e' la merce per SETTE fornitori: bizzotto, mauro_ferretti,
// oyat e web_furniture avevano gia' il badge in card (``fornitori_merce``,
// backend orders/router.py) e non erano filtrabili. Ora l'appartenenza si
// legge anche da li', quindi un fornitore nuovo nel catalogo entra nel filtro
// da solo, senza toccare il codice.
//
// ⛔ Resta vero che «chi vende» non e' «da dove e' partito il pacco»
// (``supplier_kind``): promuovere il catalogo a kind sposto' 1.106 ordini sul
// fornitore sbagliato il 2026-08-06. Qui non si promuove nulla — il filtro e'
// SOLO visualizzazione e il match e' ADDITIVO (ANY-match, come per gli ordini
// misti dal 2026-07-06): un ordine ALL SERVICE con SKU Ityhome resta ALL
// SERVICE ovunque conti, e in piu' si trova anche filtrando ITYHOME.
import { SUPPLIER_TABS } from "@/features/fornitori/components/suppliers";
import type { Order } from "./types";

/** Nome leggibile del fornitore dal suo id (kind backend o supplier del PIM).
 *  Fonte unica: la usano il badge in card e le opzioni del filtro. */
export const FORNITORE_LABEL: Record<string, string> = {
  itamoby: "ITAMOBY",
  ityhome: "ITYHOME",
  allservice: "ALL SERVICE",
  cosma: "COSMA",
  bizzotto: "BIZZOTTO",
  mauro_ferretti: "MAURO FERRETTI",
  web_furniture: "WEB FURNITURE",
  oyat: "OYAT",
  aurora: "AURORA BEDDING",
  bscom: "MAGAZZINO BS COM",
  unknown: "Forn. n/d",
};

const TAB_LABEL: Record<string, string> = Object.fromEntries(
  SUPPLIER_TABS.map((t) => [t.id, t.label]),
);

/** Etichetta di un fornitore mai visto prima: dall'id, non "n/d" — un
 *  fornitore nuovo nel PIM deve essere leggibile senza un deploy. */
export function labelFornitore(id: string): string {
  return FORNITORE_LABEL[id] ?? TAB_LABEL[id] ?? id.replace(/_/g, " ").toUpperCase();
}

/** Il valore arriva dal PIM (colonna ``supplier``, testo libero): si normalizza
 *  al confine, o "Bizzotto" e "bizzotto" diventano due opzioni. */
function normalizza(id: unknown): string {
  return typeof id === "string" ? id.trim().toLowerCase() : "";
}

/** Tutti i fornitori a cui l'ordine appartiene (id, non solo kind). */
export function fornitoriDellOrdine(order: Order): string[] {
  const ids = new Set<string>();
  const aggiungi = (id: unknown) => {
    const norm = normalizza(id);
    if (norm && norm !== "unknown") ids.add(norm);
  };
  aggiungi(order.fornitore?.kind);
  for (const kind of Object.values(order.itemSupplierKinds ?? {})) aggiungi(kind);
  // Il catalogo (PIM): SKU → chi vende. E' questa la riga che rende il filtro
  // automatico sui fornitori che un ``supplier_kind`` non ce l'hanno.
  for (const fornitore of Object.values(order.fornitoriMerce ?? {})) aggiungi(fornitore);
  for (const tab of SUPPLIER_TABS) {
    if (tab.orderFlag && order[tab.orderFlag] === true) ids.add(tab.id);
  }
  return [...ids];
}

/** L'ordine e' filtrabile sotto questo fornitore? */
export function ordineDelFornitore(order: Order, supplierId: string): boolean {
  return fornitoriDellOrdine(order).includes(supplierId);
}

/** Opzioni del menu a discesa: l'anagrafica fornitori (sempre, cosi' le voci
 *  non ballano quando cambia la finestra di date) piu' chiunque compaia negli
 *  ordini caricati — inclusi i fornitori che esistono solo nel catalogo.
 *
 *  ``selezionati`` rientra sempre: i fornitori che vivono solo nel PIM
 *  (mauro_ferretti, oyat, i prossimi) compaiono solo se hanno ordini nella
 *  finestra corrente, ma la spunta e' persistita in sessionStorage. Senza
 *  riseminarla, stringere le date faceva sparire la voce lasciando il filtro
 *  attivo e invisibile: zero ordini e niente da smarcare. */
export function opzioniFornitore(
  orders: readonly Order[],
  selezionati: ReadonlySet<string> = new Set(),
): { value: string; label: string }[] {
  const ids = new Set<string>(SUPPLIER_TABS.map((t) => t.id));
  for (const id of selezionati) if (id) ids.add(id);
  for (const order of orders) for (const id of fornitoriDellOrdine(order)) ids.add(id);
  return [...ids]
    .map((id) => ({ value: id, label: labelFornitore(id) }))
    .sort((a, b) => a.label.localeCompare(b.label, "it"));
}
